import classNames from 'classnames';
import _, { isEmpty } from 'lodash';
import React, { CSSProperties, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import Checkbox from '../../components/Checkbox/Checkbox';
import LazyScrollContainer from '../../components/LazyScrollContainer/LazyScrollContainer';
import Row from '../../components/Row/Row';
import TableCell from '../../components/TableCell/TableCell';
import TableHead from '../../components/TableHead/TableHead';
import TableRow from '../../components/TableRow/TableRow';
import { useCheckedCollection } from '../../hooks';
import Align from '../../types/enum/Align';
import Justify from '../../types/enum/Justify';
import SortDirection from '../../types/enum/SortDirection';
import TextAlign from '../../types/enum/TextAlign';
import ITableColumn from '../../types/TableColumn';
import { tableCellHeightClassNameLookup } from './constants/constants';
import './Table.scss';
import ITableProps from './TableProps';

const Table = <RowType extends object>({
  data: rows,
  cols,
  selected,
  onSelectChange,
  onCheckAllChange,
  selectKey,
  sortBy,
  sortDir,
  onSort,
  onRowClick,
  minWidth,
  strokeRows,
  strokeCols,
  header,
  footer,
  checkboxCell,
  checkboxClassName,
  emptyTemplate,
  isLoading,
  lazyLoad,
  lazyScrollSectionSize,
  lazyScrollOffset,
  lazyScrollTrigger,
  rowKey,
  stickyHeaderOffset,
  stickyFooterOffset,
  scroller,
  headerTitle,
  hideRow,
  sortIsDirty,
  headerClassName,
  footerClassName,
  className,
  rowClassName,
  getCustomRowClassName,
  cellHeight,
}: ITableProps<RowType>) => {
  const { onCheck } = useCheckedCollection(rows, selected ?? [], selectKey, handleSelectChange);

  const selectedSet = useMemo(
    () => (selected ? _.groupBy(selected, (checkedItem) => (checkedItem as any).id) : {}),
    [selected]
  );

  useEffect(() => {
    setHeaderFooterHeight();
    addEventListeners();

    return removeEventListeners;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cellHeight]);

  useEffect(() => {
    setTimeout(adjustHeaderFooter, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const headerRef = useRef<HTMLElement | null>(null);
  const headerContentRef = useRef<HTMLElement | null>(null);
  const headerSpacerRef = useRef<HTMLElement | null>(null);
  const footerRef = useRef<HTMLElement | null>(null);
  const footerContentRef = useRef<HTMLElement | null>(null);
  const footerSpacerRef = useRef<HTMLElement | null>(null);
  const bodyRef = useRef<HTMLElement | null>(null);
  const containerRef = useRef<HTMLElement | null>(null);
  const isHeaderStuck = useRef<boolean>(false);
  const isFooterStuck = useRef<boolean>(false);

  const cellHeightClassName = cellHeight ? tableCellHeightClassNameLookup[cellHeight] : null;

  function setHeaderRef(element: HTMLElement | null) {
    headerRef.current = element;
  }

  function setHeaderContentRef(element: HTMLElement | null) {
    headerContentRef.current = element;
  }

  function setHeaderSpacerRef(element: HTMLElement | null) {
    headerSpacerRef.current = element;
  }

  function setFooterRef(element: HTMLElement | null) {
    footerRef.current = element;
  }

  function setFooterContentRef(element: HTMLElement | null) {
    footerContentRef.current = element;
  }

  function setFooterSpacerRef(element: HTMLElement | null) {
    footerSpacerRef.current = element;
  }

  function setBodyRef(element: HTMLElement | null) {
    bodyRef.current = element;
  }

  function setContainerRef(element: HTMLElement | null) {
    containerRef.current = element;
  }

  function addEventListeners() {
    if (header === 'sticky') {
      scroller.addEventListener('scroll', positionHeader);

      if (minWidth && containerRef.current) {
        containerRef.current.addEventListener('scroll', adjustHeaderScroll);
      }
    }
    if (footer === 'sticky') {
      scroller.addEventListener('scroll', positionFooter);

      if (minWidth && containerRef.current) {
        containerRef.current.addEventListener('scroll', adjustFooterScroll);
      }
    }
    window.addEventListener('resize', adjustHeaderFooter);
  }

  function removeEventListeners() {
    scroller.removeEventListener('scroll', positionHeader);
    scroller.removeEventListener('scroll', positionFooter);

    if (containerRef.current) {
      containerRef.current.removeEventListener('scroll', adjustHeaderScroll);
      containerRef.current.removeEventListener('scroll', adjustFooterScroll);
    }

    window.removeEventListener('resize', adjustHeaderFooter);
  }

  function adjustHeaderScroll() {
    if (headerRef.current && containerRef.current) {
      headerRef.current.scrollLeft = containerRef.current.scrollLeft;
    }
  }

  function adjustFooterScroll() {
    if (footerRef.current && containerRef.current) {
      footerRef.current.scrollLeft = containerRef.current.scrollLeft;
    }
  }

  function resizeHeader() {
    if (isHeaderStuck.current && headerRef.current) {
      if (headerContentRef.current && bodyRef.current) {
        headerContentRef.current.style.width = bodyRef.current.offsetWidth + 'px';
      }

      if (headerRef.current && containerRef.current) {
        headerRef.current.style.width = containerRef.current.offsetWidth + 'px';
      }

      adjustHeaderScroll();
    }
  }

  function resizeFooter() {
    if (isFooterStuck.current && footer) {
      if (footerContentRef.current && bodyRef.current) {
        footerContentRef.current.style.width = bodyRef.current.getBoundingClientRect().width + 'px';
      }

      if (footerRef.current && containerRef.current) {
        footerRef.current.style.width = containerRef.current.offsetWidth + 'px';
      }
    }
  }

  function adjustHeaderFooter() {
    if (header === 'sticky') {
      resizeHeader();
      positionHeader();
    }

    if (footer === 'sticky') {
      resizeFooter();
      positionFooter();
    }

    setHeaderFooterHeight();
  }

  function setHeaderFooterHeight() {
    if (headerRef.current && headerSpacerRef.current) {
      headerSpacerRef.current.style.height = headerRef.current.offsetHeight + 'px';
    }

    if (footerRef.current && footerSpacerRef.current) {
      footerSpacerRef.current.style.height = footerRef.current.offsetHeight + 'px';
    }
  }

  function positionHeader() {
    if (headerRef.current && headerSpacerRef.current && containerRef.current) {
      const spacerRect = headerSpacerRef.current.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();
      const offset = stickyHeaderOffset;

      if (spacerRect.top - offset < 0 && containerRect.bottom >= 0) {
        // if the top of the spacer is above the viewport && the table is within the viewport
        const headerRect = headerRef.current.getBoundingClientRect();

        if (!isHeaderStuck.current) {
          isHeaderStuck.current = true;
          resizeHeader();
          headerRef.current.classList.add('sticky');
          headerRef.current.style.top = offset + 'px';
        }

        if (minWidth) {
          adjustHeaderScroll();
        }

        if (bodyRef.current) {
          const bodyRect = bodyRef.current.getBoundingClientRect();

          if (bodyRect.bottom - offset - headerRect.height <= 0) {
            // if the fixed header has hit the bottom of table
            headerRef.current.style.top = bodyRect.bottom - spacerRect.height + 'px';
          } else {
            // fixed header is between top of viewport and bottom of table
            headerRef.current.style.top = offset + 'px';
          }
        } else {
          // fixed header is between top of viewport and bottom of table
          headerRef.current.style.top = offset + 'px';
        }
      } else {
        if (isHeaderStuck.current) {
          isHeaderStuck.current = false;
          headerRef.current.classList.remove('sticky');
          headerRef.current.style.top = '';
          headerRef.current.style.width = '';
          headerRef.current.style.position = 'absolute';

          if (headerContentRef.current) {
            headerContentRef.current.style.width = '';
          }
        }
      }
    }
  }

  function positionFooter() {
    if (footerRef.current && bodyRef.current && footerSpacerRef.current) {
      const spacerRect = footerSpacerRef.current.getBoundingClientRect();
      const bodyRect = bodyRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const offset = stickyFooterOffset;

      if (spacerRect.bottom + offset >= windowHeight && bodyRect.top <= windowHeight) {
        // if the bottom of the spacer is below the viewport and the table is within the viewport
        const footerRect = footerRef.current.getBoundingClientRect();

        if (!isFooterStuck.current) {
          isFooterStuck.current = true;
          resizeFooter();
          footerRef.current.classList.add('sticky');
          footerRef.current.style.bottom = offset + 'px';
        }

        if (minWidth) {
          adjustFooterScroll();
        }

        if (bodyRect.top + offset > windowHeight - footerRect.height) {
          // if the fixed footer has hit the top of the table
          footerRef.current.style.bottom = windowHeight - bodyRect.top - spacerRect.height + 'px';
        } else {
          footerRef.current.style.bottom = offset + 'px';
        }
      } else {
        if (isFooterStuck.current) {
          isFooterStuck.current = false;

          footerRef.current.classList.remove('sticky');
          footerRef.current.style.bottom = '';
          footerRef.current.style.width = '';
          footerRef.current.style.position = 'absolute';

          if (footerContentRef.current) {
            footerContentRef.current.style.width = '';
          }
        }
      }
    }
  }

  function handleSort(column: ITableColumn<RowType>) {
    let newSortDir: SortDirection = SortDirection.ASCENDING;

    if (column.sort && onSort) {
      if (column.key === sortBy) {
        newSortDir = sortDir === SortDirection.ASCENDING ? SortDirection.DESCENDING : SortDirection.ASCENDING;
      }
      onSort({ sortBy: column.key, sortDir: newSortDir });
    }
  }

  function handleSelectChange(checkedRows: RowType[]) {
    if (onSelectChange) {
      onSelectChange(checkedRows);
    }
  }

  function handleGlobalCheckboxChanged(isChecked: boolean) {
    const checked = isChecked ? rows : [];
    handleSelectChange(checked);

    if (onCheckAllChange) {
      onCheckAllChange(isChecked);
    }
  }

  function handleRowClick(row: RowType, event: React.MouseEvent) {
    if (onRowClick) {
      onRowClick(row, event);
    }
  }

  // this weird too
  function handleCellClick(
    row: RowType,
    column: ITableColumn<RowType>,
    callback: (row: RowType, column: ITableColumn<RowType>, event: React.MouseEvent) => void,
    event: React.MouseEvent
  ) {
    if (callback) {
      callback(row, column, event);
    }
  }

  const stopPropagation = useCallback((event: { stopPropagation: () => void }) => {
    event.stopPropagation();
  }, []);

  const paddingClassNameTester = useRef(/(px|py|p)-([1-9]|1[0-2])/g);

  function getClassNameWithPadding(className: string) {
    const cellHeightSet = cellHeightClassName !== null && cellHeightClassName !== undefined;

    if (!cellHeightSet) {
      return isEmpty(className) ? 'p-4' : className;
    }

    const hasPaddingInClassName = paddingClassNameTester.current.test(className);
    return classNames({ 'px-4': !hasPaddingInClassName, [cellHeightClassName!]: cellHeightSet }, className);
  }

  function renderHeader() {
    if (header === 'none') {
      return null;
    }

    const headerCheckboxClassName = getClassNameWithPadding(checkboxClassName?.toString() ?? '');

    return (
      <>
        <div ref={setHeaderRef} className={classNames('table-header', headerClassName)}>
          {headerTitle}
          <table ref={setHeaderContentRef}>
            <thead>
              <TableRow>
                {selected && onSelectChange && (
                  <TableCell
                    align={TextAlign.CENTER}
                    className={`table-cell-checkbox no-print ${headerCheckboxClassName}`}
                  >
                    {onCheckAllChange ? (
                      <Checkbox
                        type="global"
                        onChange={handleGlobalCheckboxChanged}
                        checked={selected.length !== 0 && selected.length === rows.length}
                      />
                    ) : (
                      <></>
                    )}
                  </TableCell>
                )}
                {cols.map((col) => {
                  function sort() {
                    handleSort(col);
                  }
                  const passedCheckboxClassName = getClassNameWithPadding(col.headerClassName?.toString() ?? '');

                  return (
                    <TableHead
                      key={col.key}
                      sortable={col.sort ?? false}
                      sortDir={sortDir}
                      isActiveSort={col.key === sortBy}
                      onSort={col.sort ? sort : undefined}
                      priority={col.priority}
                      size={col.size}
                      align={col.align}
                      tooltip={col.tooltip}
                      className={passedCheckboxClassName}
                      style={col.headerTextWrap ? textWrapProps : undefined}
                    >
                      {col.header ? col.header(col) : col.title}
                    </TableHead>
                  );
                })}
              </TableRow>
            </thead>
          </table>
        </div>
        <div className="table-header-spacer" ref={setHeaderSpacerRef} />
      </>
    );
  }

  const renderRow = (row: RowType, index: number) => {
    const defaultCheckboxTemplate = (checkbox: ReactNode) => checkbox;
    const defaultCellTemplate = (r: RowType, col: ITableColumn<RowType>) => _.get(r, col.key);

    const onRowCheckboxChanged = (isChecked: boolean) => {
      onCheck(row, isChecked);
    };

    const id = (row as { id: any }).id;
    const isSelected = selectedSet[id] ? true : false;

    const checkboxTemplate = checkboxCell ? checkboxCell : defaultCheckboxTemplate;

    if (hideRow && hideRow(row)) {
      return null;
    }

    const key: number | string = row[rowKey] ? (row[rowKey] as unknown as string | number) : index;
    const className = rowClassName ?? (getCustomRowClassName ? getCustomRowClassName(row) : undefined);
    const rowClasses = classNames(isSelected ? 'table-row-selected' : undefined, className);
    const passedCheckboxClassName = getClassNameWithPadding(checkboxClassName?.toString() ?? '');

    const cellClassName = classNames('table-cell-checkbox', 'no-print', passedCheckboxClassName);

    return (
      <TableRow
        row={row}
        key={key}
        onClick={onRowClick !== undefined ? handleRowClick : undefined}
        className={rowClasses}
      >
        {selected && onSelectChange && (
          <TableCell align={TextAlign.CENTER} onClick={stopPropagation} className={cellClassName}>
            {checkboxTemplate(
              <Checkbox onChange={onRowCheckboxChanged} onClick={stopPropagation} checked={isSelected} />,
              row
            )}
          </TableCell>
        )}
        {cols.map((col) => {
          const template = col.cell ? col.cell : defaultCellTemplate;

          function onCellClick(event: React.MouseEvent) {
            handleCellClick(row, col, col.onClick!, event);
          }

          const getCustomCellClassNameResult = col.getCustomCellClassName?.(row) ?? null;

          const cellClassName = getClassNameWithPadding(classNames(getCustomCellClassNameResult, col.cellClassName));

          return (
            <TableCell
              key={col.key}
              priority={col.priority}
              size={col.size}
              align={col.align}
              disabled={col.disabled}
              className={cellClassName}
              style={col.textWrap ? textWrapProps : undefined}
              onClick={col.onClick !== undefined ? onCellClick : undefined}
            >
              {template(row, col)}
            </TableCell>
          );
        })}
      </TableRow>
    );
  };

  const renderTemplate = useCallback(
    (rs: ReactNode | ReactNode[]) => {
      return (
        <table className="table-body" ref={setBodyRef}>
          <thead className="printer-head">
            <tr>
              {cols.map((col) => {
                const passedCheckboxClassName = getClassNameWithPadding(col.headerClassName?.toString() ?? '');
                return (
                  <TableHead key={col.key} size={col.size} align={col.align} className={passedCheckboxClassName}>
                    {col.header ? col.header(col) : col.title}
                  </TableHead>
                );
              })}
            </tr>
          </thead>
          <tbody>{rs}</tbody>
        </table>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cols]
  );

  function renderBody() {
    if (lazyLoad) {
      return (
        <LazyScrollContainer
          data={rows}
          sectionSize={lazyScrollSectionSize}
          offset={lazyScrollOffset}
          template={renderTemplate}
          renderRow={renderRow}
          lazyScrollTrigger={lazyScrollTrigger}
        />
      );
    } else {
      return (
        <table className="table-body" ref={setBodyRef}>
          <thead className="printer-head">
            <tr>
              {cols.map((col) => {
                const passedCheckboxClassName = getClassNameWithPadding(col.headerClassName?.toString() ?? '');
                return (
                  <TableHead key={col.key} size={col.size} align={col.align} className={passedCheckboxClassName}>
                    {col.header ? col.header(col) : col.title}
                  </TableHead>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              return renderRow(row, index);
            })}
          </tbody>
        </table>
      );
    }
  }

  function renderFooter() {
    if (footer === 'none') {
      return null;
    }

    return (
      <>
        <div className={classNames('table-footer', footerClassName)} ref={setFooterRef}>
          <table ref={setFooterContentRef}>
            <tbody>
              <TableRow>
                {selected && onSelectChange && <TableCell className="table-cell-checkbox no-print" />}
                {cols.map((col) => {
                  const passedCheckboxClassName = getClassNameWithPadding(col.footerClassName?.toString() ?? '');

                  return (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      size={col.size}
                      priority={col.priority}
                      style={col.textWrap ? textWrapProps : undefined}
                      className={passedCheckboxClassName}
                    >
                      {col.footer && col.footer(col)}
                    </TableCell>
                  );
                })}
              </TableRow>
            </tbody>
          </table>
        </div>
        <div ref={setFooterSpacerRef} />
      </>
    );
  }

  function renderEmptyState() {
    if (emptyTemplate) {
      return emptyTemplate;
    }

    return (
      <Row justify={Justify.CENTER} align={Align.CENTER} className="default-empty-template">
        No data
      </Row>
    );
  }

  function renderContent() {
    if (rows.length !== 0 || isLoading) {
      return (
        <div className="table-inner-wrapper" style={{ minWidth: minWidth || undefined }}>
          {renderHeader()}
          {renderBody()}
          {renderFooter()}
        </div>
      );
    } else {
      return renderEmptyState();
    }
  }

  const classes = classNames(
    {
      'table-wrapper': true,
      'table-stroke-rows': strokeRows,
      'table-stroke-cols': strokeCols,
      'table-sorted': sortIsDirty,
    },
    className
  );

  return (
    <div className={classes} ref={setContainerRef}>
      {renderContent()}
    </div>
  );
};

const textWrapProps: CSSProperties = {
  whiteSpace: 'unset',
};

Table.defaultProps = {
  sortDir: 'ASC',
  strokeRows: true,
  strokeCols: true,
  header: 'standard',
  footer: 'none',
  isLoading: false,
  lazyScrollSectionSize: 1,
  lazyScrollOffset: 500,
  rowKey: 'id',
  lazyLoad: false,
  stickyHeaderOffset: 0,
  stickyFooterOffset: 0,
  scroller: document,
  // This isn't necessarily a keyof RowType
  // Table should be broken apart into multiple components to facilitate checking vs not checking
  selectKey: 'id',
};

export default Table;
